import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  CardHeader,
  Input,
} from "reactstrap";
import { useHistory } from "react-router-dom";

import { ChapterDto, OrganizingChapterDto } from "../../interfaces/chapter";
import DeleteModal from "../../components/Common/DeleteModal";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteChapter,
  deleteChapterConsent,
  deleteLesson,
  deleteLessonConsent,
  getOrganizingChapters,
  toggleChapterState,
  toggleLessonState,
} from "../../store/actions";
import {
  LessonDto,
  PaginateQuery,
  State,
  UserProfileDto,
} from "../../interfaces";
import { NoResultFound } from "../../components/Common";
import { Paginator, TableSpinner } from "../../components";
import { RoleEnum } from "../../enums";
import { AddEditChapterModal, AddEditLessonModal } from "../Exam";
import { Paginated } from "../../models";

type TProps = {
  subjectId: string;
};
export const OrganizingChaptersPage: React.FC<TProps> = ({ subjectId }) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery(10);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  //#endregion

  //#region redux state
  const { loading: chaptersLoading, response: chaptersData } = useSelector(
    (state: any): State<Paginated<OrganizingChapterDto>> =>
      state.chapter.organizingChapters
  );

  useSelector((state: any): State<any> => state.chapter.toggleLessonState);

  const {
    loading: deleteChapterLoading,
    error: deleteChapterError,
    success: deleteChapterSuccess,
  } = useSelector(
    (state: any): State<ChapterDto> => state.chapter.deleteChapter
  );

  const {
    loading: deleteLessonLoading,
    error: deleteLessonError,
    success: deleteLessonSuccess,
  } = useSelector((state: any): State<LessonDto> => state.lesson.deleteLesson);

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );
  //#endregion

  //#region internal state
  const [deletingChapterId, setDeletingChapterId] = useState<string | null>(
    null
  );
  const [showChapterDeleteModal, setShowChapterDeleteModal] =
    useState<boolean>(false);
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [showChapterAddEditModal, setShowChapterAddEditModal] =
    useState<boolean>(false);
  const [showLessonDeleteModal, setShowLessonDeleteModal] =
    useState<boolean>(false);
  const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null);
  const [showLessonAddEditModal, setShowLessonAddEditModal] =
    useState<boolean>(false);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [addingLessonChapterId, setAddingLessonChapterId] = useState<
    string | null
  >(null);
  //#endregion

  document.title = `${t("documentTitle", { pageName: "organizing" })}`;

  useEffect(() => {
    dispatch(getOrganizingChapters(subjectId, paginateQuery, history));
  }, [paginateQuery, subjectId]);

  useEffect(() => {
    if (!deleteLessonLoading && deleteLessonSuccess) {
      setDeletingLessonId(null);
      setShowLessonDeleteModal(false);
      dispatch(getOrganizingChapters(subjectId, paginateQuery, history));
    }
  }, [deleteLessonLoading]);

  useEffect(() => {
    if (!deleteChapterLoading && deleteChapterSuccess) {
      setDeletingChapterId(null);
      setShowChapterDeleteModal(false);
      dispatch(getOrganizingChapters(subjectId, paginateQuery, history));
    }
  }, [deleteChapterLoading]);

  //#endregion

  //#region handlers

  const onAddChapter = () => {
    setShowChapterAddEditModal(true);
  };

  const onEditChapter = (id: string) => {
    setEditingChapterId(id);
    setShowChapterAddEditModal(true);
  };

  const onToggleChapterState = (id: string) => {
    dispatch(toggleChapterState(id, history));
  };

  const onToggleLessonState = (chapterId: string, lessonId: string) => {
    dispatch(toggleLessonState(chapterId, lessonId));
  };

  const onDeleteChapter = (id: string) => {
    setDeletingChapterId(id);
    setShowChapterDeleteModal(true);
    dispatch(deleteChapterConsent(id));
  };

  const onDeleteChapterConfirm = () => {
    dispatch(deleteChapter(deletingChapterId as string, history));
    setDeletingChapterId(null);
  };

  const onAddLesson = (chapterId: string) => {
    setAddingLessonChapterId(chapterId);
    setShowLessonAddEditModal(true);
  };

  const onEditLesson = (id: string, chapterId: string) => {
    setAddingLessonChapterId(chapterId);
    setEditingLessonId(id);
    setShowLessonAddEditModal(true);
  };

  const onDeleteLesson = (id: string) => {
    setDeletingLessonId(id);
    setShowLessonDeleteModal(true);
    dispatch(deleteLessonConsent(id));
  };

  const onDeleteLessonConfirm = () => {
    dispatch(deleteLesson(deletingLessonId as string, history));
    setDeletingLessonId(null);
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  //#endregion

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{t("organizing")}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button color="success" onClick={() => onAddChapter()}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addChapter")}
              </Button>
            </div>
          </Col>
        </Row>

        {chaptersLoading && <TableSpinner />}
        {!chaptersLoading && chaptersData && (
          <Row>
            {chaptersData &&
              chaptersData.data &&
              chaptersData.data.map((item: OrganizingChapterDto, key) => (
                <Col lg={12} key={key}>
                  <Card>
                    <CardHeader>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex">
                          {item.sequenceNumber && item.sequenceNumber > 0 && (
                            <h4>{item.sequenceNumber} - </h4>
                          )}
                          &nbsp;
                          <h4>
                            {i18n.language === "ar"
                              ? item.name.ar
                              : item.name.en}{" "}
                          </h4>
                        </div>

                        <div>
                          {item.isSpecial && (
                            <span
                              className="badge"
                              style={{ backgroundColor: "gold" }}
                            >
                              {t("golden")}
                            </span>
                          )}
                          &nbsp;
                          {profile &&
                            profile.role.key == RoleEnum.SUPER_ADMIN && (
                              <Button
                                className={`btn btn-sm ${
                                  item.isActive ? "btn-danger" : ""
                                }`}
                                onClick={() => onToggleChapterState(item.id)}
                              >
                                {item.isActive
                                  ? t("inactivate")
                                  : t("activate")}
                              </Button>
                            )}
                          &nbsp;
                          <Button
                            title={t("editItem", {
                              item: "chapter",
                            })}
                            className="btn btn-sm btn-success"
                            onClick={() => onEditChapter(item.id)}
                          >
                            {t("edit")}
                          </Button>
                          &nbsp;
                          <Button
                            title={t("deleteItem", { item: "chapter" })}
                            className="btn btn-sm btn-danger"
                            onClick={() => onDeleteChapter(item.id)}
                          >
                            {t("delete")}
                          </Button>
                          &nbsp;
                          <Button
                            title={t("deleteItem", { item: "chapter" })}
                            className="btn btn-sm btn-primary"
                            onClick={() => onAddLesson(item.id)}
                          >
                            {t("addItem", { item: "lesson" })}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Row className="g-1 mb-3">
                        <div
                          className="table-responsive"
                          style={{ maxHeight: "500px", overflowY: "auto" }}
                        >
                          <Table className="table-bordered table-nowrap align-middle mb-0">
                            <thead
                              style={{ position: "sticky", top: 0, zIndex: 1 }}
                            >
                              <tr>
                                <th
                                  scope="col"
                                  className="text-left table-secondary"
                                >
                                  {t("lesson")}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {t("lecture")}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {t("flashcard")}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {t("question")}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {t("freeLesson")}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {t("action")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.lessons.map((lessonItem, lessonKey) => (
                                <tr key={lessonKey}>
                                  <td className="fw-medium text-left">
                                    {i18n.language === "ar"
                                      ? lessonItem.name.ar
                                      : lessonItem.name.en}
                                    <span>
                                      &nbsp;
                                      {lessonItem.sequenceNumber &&
                                        lessonItem.sequenceNumber > 0 && (
                                          <Badge color={`${"secondary"}`}>
                                            {lessonItem.sequenceNumber}
                                          </Badge>
                                        )}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <Input
                                      disabled
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={lessonItem.enableInLecture}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <Input
                                      disabled
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={lessonItem.enableInFlashcard}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <Input
                                      disabled
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={lessonItem.enableInQuestion}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <Input
                                      disabled
                                      className="form-check-input danger"
                                      type="checkbox"
                                      checked={lessonItem.isFree}
                                    />
                                  </td>
                                  <td className="fw-medium text-center">
                                    {profile &&
                                      profile.role.key ==
                                        RoleEnum.SUPER_ADMIN && (
                                        <Button
                                          className={`btn btn-sm ${
                                            lessonItem.isActive
                                              ? "btn-danger"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            onToggleLessonState(
                                              item.id,
                                              lessonItem.id
                                            )
                                          }
                                        >
                                          {lessonItem.isActive
                                            ? t("inactivate")
                                            : t("activate")}
                                        </Button>
                                      )}
                                    &nbsp;
                                    <Button
                                      title={t("editItem", {
                                        item: "lesson",
                                      })}
                                      className="btn btn-sm btn-success"
                                      onClick={() =>
                                        onEditLesson(
                                          lessonItem.id,
                                          lessonItem.id
                                        )
                                      }
                                    >
                                      {t("edit")}
                                    </Button>
                                    &nbsp;
                                    <Button
                                      title={t("deleteItem", {
                                        item: "lesson",
                                      })}
                                      className="btn btn-sm btn-danger"
                                      onClick={() =>
                                        onDeleteLesson(lessonItem.id)
                                      }
                                    >
                                      {t("delete")}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              ))}
          </Row>
        )}
        {!chaptersLoading &&
          (!chaptersData ||
            !chaptersData.data ||
            chaptersData.data.length == 0) && <NoResultFound />}
        {!chaptersLoading &&
          chaptersData &&
          chaptersData.data &&
          chaptersData.data.length > 0 && (
            <Paginator
              itemsOnCurrentPage={chaptersData?.data?.length || 0}
              itemsPerPage={chaptersData?.meta?.itemsPerPage as number}
              totalItems={chaptersData?.meta?.totalItems || 0}
              currentPage={chaptersData?.meta?.currentPage || 1}
              totalPages={chaptersData?.meta?.totalPages || 0}
              onChangePage={(pageToChange: number) =>
                onChangePage(pageToChange)
              }
            />
          )}
        <DeleteModal
          loading={deleteChapterLoading}
          show={showChapterDeleteModal}
          onDeleteClick={() => onDeleteChapterConfirm()}
          onCloseClick={() => {
            setShowChapterDeleteModal(false);
          }}
          error={deleteChapterError}
        />
        <AddEditChapterModal
          subjectId={subjectId}
          id={editingChapterId}
          show={showChapterAddEditModal}
          onCloseClick={() => {
            setShowChapterAddEditModal(false);
            dispatch(getOrganizingChapters(subjectId, paginateQuery, history));
            setEditingChapterId(null);
          }}
        />
        <DeleteModal
          loading={deleteLessonLoading}
          show={showLessonDeleteModal}
          onDeleteClick={() => onDeleteLessonConfirm()}
          onCloseClick={() => {
            setShowLessonDeleteModal(false);
          }}
          error={deleteLessonError}
        />
        {addingLessonChapterId && (
          <AddEditLessonModal
            chapterId={addingLessonChapterId}
            id={editingLessonId}
            show={showLessonAddEditModal}
            onCloseClick={() => {
              setShowLessonAddEditModal(false);
              dispatch(
                getOrganizingChapters(subjectId, paginateQuery, history)
              );
              setEditingLessonId(null);
            }}
          />
        )}
      </Container>
    </React.Fragment>
  );
};

function SimpleBadge(props: any) {
  const { classes } = props;
  return (
    <div>
      <Badge
        classes={{ badge: { backgroundColor: "#00AFD7", color: "white" } }}
        className={classes.margin}
        badgeContent={10}
      ></Badge>
    </div>
  );
}
